import type { AppConfig } from '@/@types/manifest'
import { APP_GET, MANIFESTS } from '@/constants/api'
import { get, post } from '@/utils/request'
import type { QueryKey, UseQueryOptions, UseSuspenseQueryOptions } from '@tanstack/react-query'
import { get as _get, cloneDeep } from 'lodash-es'
import type { AnimationSegment } from 'lottie-web'
import { set } from 'wild-wild-path'
import { find, flatten, include } from 'wild-wild-utils'
import queryClient from '../queryClient'

export const MANIFEST_QUERY_OPTIONS: UseSuspenseQueryOptions<AppConfig> = {
	queryKey: [MANIFESTS],
	queryFn: () => get(MANIFESTS).then((res) => res.data.conf as AppConfig),
	staleTime: Number.POSITIVE_INFINITY,
}

export type LikeLottieDataMap = Record<
	keyof AppConfig['message']['likeLottie'],
	{
		selectStart: number
		selectEnd: number
		selectSegment: AnimationSegment
		unselectStart: number
		unselectEnd: number
		unselectSegment: AnimationSegment
		data: Record<string, unknown>
	}
>

export const LIKE_LOTTIE_DATA_QUERY_OPTIONS: UseQueryOptions<LikeLottieDataMap> = {
	queryKey: ['LIKE_LOTTIE_DATA'],
	queryFn: async () => {
		const appConfig = await queryClient.ensureQueryData(MANIFEST_QUERY_OPTIONS)
		const likeLottieDataMap = await getLikeLottieDataMap(appConfig)
		return likeLottieDataMap
	},
	staleTime: Number.POSITIVE_INFINITY,
}

interface Marker {
	cm: string
	tm: number
}
export async function getLikeLottieDataMap(appConfig: AppConfig): Promise<LikeLottieDataMap> {
	const result: LikeLottieDataMap = {}

	for (const [key, url] of Object.entries(appConfig.message.likeLottie)) {
		const res = await window.fetch(url)
		const json = await res.json()

		const selectStart: number =
			json.markers?.find((item: Marker) => item.cm === 'selectStart')?.tm ?? 0
		const selectEnd: number = json.markers?.find((item: Marker) => item.cm === 'selectEnd')?.tm ?? 0
		const unselectStart: number =
			json.markers?.find((item: Marker) => item.cm === 'unselectStart')?.tm ?? 0
		const unselectEnd: number =
			json.markers?.find((item: Marker) => item.cm === 'unselectEnd')?.tm ?? 0

		const data = cloneDeep(json)
		modifyOutlineTint(data, true)

		result[key] = {
			selectStart,
			selectEnd,
			selectSegment: [selectStart, selectEnd] as AnimationSegment,
			unselectStart,
			unselectEnd,
			unselectSegment: [unselectStart, unselectEnd] as AnimationSegment,
			data,
		}
	}

	return result
}

function modifyOutlineTint(json: Record<string, unknown>, isSelected: boolean) {
	const outlineName = isSelected ? 'selected outline' : 'unselected outline'
	const outlineEntries = find(json, '**.nm', (nm) => nm.value === outlineName, {
		entries: true,
	})
	if (!outlineEntries) {
		return
	}
	const outlinePath = outlineEntries.path.slice(0, -1)

	for (const [key, value] of Object.entries(
		flatten(
			include(_get(json, outlinePath), '**.nm', (nm) => nm.value === 'tint', {
				entries: true,
			})
		)
	)) {
		const path = key.split('.').slice(0, -1)
		const tintPath = [...outlinePath, ...path, 'c', 'k']

		set(json, tintPath, [0.8941, 0.3451, 0.2431, 0], {
			mutate: true,
		})
	}
}

export interface VideoUploadLimit {
	maxDuration: number
	maxSize: number
}
export const VIDEO_UPLOAD_LIMIT_QUERY_KEY: QueryKey = [APP_GET, 'VIDEO_UPLOAD_LIMIT']
export const VIDEO_UPLOAD_LIMIT_QUERY_OPTIONS: UseQueryOptions<VideoUploadLimit> = {
	queryKey: VIDEO_UPLOAD_LIMIT_QUERY_KEY,
	queryFn: () =>
		post<{
			data: {
				uploadVideoLimit: VideoUploadLimit
			}
		}>(APP_GET, { keys: ['uploadVideoLimit'] }).then((res) => res.data.data.uploadVideoLimit),
	enabled: false,
}
